import React, { useEffect, useState } from "react";
import { BedDouble, Users, CheckCircle2, Clock } from "lucide-react";
import { api } from "../api";

export function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Prospectos totales", value: stats.total_leads, icon: Users },
        { label: "Habitaciones", value: stats.total_rooms, icon: BedDouble },
        { label: "Disponibles", value: stats.rooms_by_status?.Disponible ?? 0, icon: CheckCircle2 },
        {
          label: "Presupuesto promedio",
          value: stats.avg_budget ? `$${Number(stats.avg_budget).toLocaleString("es-MX")}` : "—",
          icon: Clock,
        },
      ]
    : [];

  const roomEntries = stats
    ? Object.entries(stats.leads_by_room || {}).sort((a, b) => b[1] - a[1])
    : [];

  const formatDay = (d) => {
    if (!d) return "—";
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("es-MX", { dateStyle: "medium" });
    } catch {
      return d;
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold text-black">Dashboard</h1>
      <p className="mt-2 text-sm font-light text-black/50">Resumen general del sistema.</p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-[#B2967D]/25 p-6" data-testid={`stat-${c.label}`}>
            <c.icon size={24} strokeWidth={1.5} className="text-[#7D5A44]" />
            <div className="mt-4 text-3xl font-bold text-black">{c.value}</div>
            <div className="mt-1 text-xs font-light text-[#B2967D] uppercase tracking-wide">{c.label}</div>
          </div>
        ))}
      </div>

      {stats && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#B2967D]/25 p-6">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#B2967D]">Prospectos por estatus</h3>
            <div className="mt-4 space-y-2">
              {Object.entries(stats.leads_by_status).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="font-light text-black/70">{k}</span>
                  <span className="font-bold text-black">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#B2967D]/25 p-6">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#B2967D]">Habitaciones por estatus</h3>
            <div className="mt-4 space-y-2">
              {Object.entries(stats.rooms_by_status).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="font-light text-black/70">{k}</span>
                  <span className="font-bold text-black">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Prospectos por habitación de interés */}
          <div className="bg-white border border-[#B2967D]/25 p-6" data-testid="stat-leads-by-room">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#B2967D]">Prospectos por habitación de interés</h3>
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {roomEntries.length === 0 && <p className="text-sm font-light text-black/40">Sin datos todavía.</p>}
              {roomEntries.map(([room, count]) => (
                <div key={room} className="flex items-center gap-3">
                  <span className="w-40 shrink-0 text-sm font-light text-black/70 truncate">{room}</span>
                  <div className="flex-1 bg-[#F5F1EA] h-2">
                    <div
                      className="bg-[#7D5A44] h-2"
                      style={{ width: `${stats.total_leads ? (count / stats.total_leads) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="w-6 text-right text-sm font-bold text-black">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Próximas fechas de entrada */}
          <div className="bg-white border border-[#B2967D]/25 p-6" data-testid="stat-upcoming-entries">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#B2967D]">Próximas fechas de entrada solicitadas</h3>
            <div className="mt-4 space-y-3">
              {(!stats.upcoming_entries || stats.upcoming_entries.length === 0) && (
                <p className="text-sm font-light text-black/40">No hay fechas próximas registradas.</p>
              )}
              {(stats.upcoming_entries || []).map((e, i) => (
                <div key={i} className="flex items-center justify-between border-b border-[#B2967D]/20 pb-2">
                  <div>
                    <div className="text-sm font-bold text-black">{e.nombre}</div>
                    <div className="text-xs font-light text-[#B2967D]">{e.habitacion_interes || "Sin especificar"}</div>
                  </div>
                  <span className="text-sm font-light text-[#7D5A44]">{formatDay(e.fecha_entrada)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
